import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { isHttpUrl, serializeJsonLd, toPostalAddress } from "./schemaUtils";
import { NoticeBox } from "@/components/NoticeBox";
import { copyText } from "@/lib/copyText";

type SchemaType =
  "Article" | "Product" | "FAQPage" | "LocalBusiness" | "Recipe";

const TOOL_INFO_CONTENT = {
  what: `Generates JSON-LD structured data (schema markup) for Article, Product, FAQ, LocalBusiness, and Recipe content types, which helps search engines understand and potentially display richer results for a page.`,
  how: `Choose the schema type that matches your content, fill in the relevant fields, and copy the generated script tag into your page.`,
  tip: `Structured data helps search engines understand a page, but it does not guarantee any special result listing, and FAQ listings in particular are limited by Google to certain sites. If a page already has a natural FAQ section, adding matching schema markup is one of the higher-leverage quick wins here.`,
};

/** Parses a simple ISO 8601 duration (PT#H#M format) into total minutes,
 * and the reverse -- used to correctly compute totalTime as prepTime +
 * cookTime rather than just repeating one of them, which Google's own
 * Recipe documentation expects as an exact duration, not an approximation. */
function parseISODurationMinutes(iso: string): number {
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 0;
  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  return hours * 60 + minutes;
}
function minutesToISODuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `PT${h}H${m}M`;
  if (h > 0) return `PT${h}H`;
  return `PT${m}M`;
}
function sumISODurations(a: string, b: string): string {
  return minutesToISODuration(parseISODurationMinutes(a) + parseISODurationMinutes(b));
}

export default function SchemaGenerator() {
  const [type, setType] = useState<SchemaType>("Article");

  // Article
  const [articleTitle, setArticleTitle] = useState(
    "How to Convert PDF to Word"
  );
  const [articleAuthor, setArticleAuthor] = useState("FreeDailyPro");
  const [articleDate, setArticleDate] = useState("2026-07-20");
  const [articleModified, setArticleModified] = useState("");
  const [articlePublisher, setArticlePublisher] = useState("");
  const [publisherLogo, setPublisherLogo] = useState("");
  const [authorType, setAuthorType] = useState<"Person" | "Organization">("Person");
  const [articleImage, setArticleImage] = useState("");

  // Product
  const [productName, setProductName] = useState("Wireless Mouse");
  const [productPrice, setProductPrice] = useState("29.99");
  const [productCurrency, setProductCurrency] = useState("USD");
  const [productAvailability, setProductAvailability] = useState("InStock");
  const [productRating, setProductRating] = useState("4.5");
  const [productReviewCount, setProductReviewCount] = useState("120");

  // FAQ -- an array of {question, answer} pairs with real add/remove
  // controls, not a single pipe-delimited textarea. Each pair also needs
  // both question and answer filled to be included in the output, so a
  // half-finished row doesn't silently produce broken schema.
  const [faqPairs, setFaqPairs] = useState<{ id: number; q: string; a: string }[]>([
    { id: 1, q: "What is FreeDailyPro?", a: "An online toolkit." },
    { id: 2, q: "Do I need to sign up?", a: "No, most tools require no signup." },
  ]);
  const [nextFaqId, setNextFaqId] = useState(3);
  const addFaqPair = () => {
    setFaqPairs(prev => [...prev, { id: nextFaqId, q: "", a: "" }]);
    setNextFaqId(id => id + 1);
  };
  const removeFaqPair = (id: number) => setFaqPairs(prev => prev.filter(p => p.id !== id));
  const updateFaqPair = (id: number, field: "q" | "a", value: string) =>
    setFaqPairs(prev => prev.map(p => (p.id === id ? { ...p, [field]: value } : p)));

  // LocalBusiness
  const [bizName, setBizName] = useState("Example Cafe");
  const [bizAddress, setBizAddress] = useState(
    "123 Main St, Raleigh, NC 27601"
  );
  const [bizPhone, setBizPhone] = useState("+1-919-555-0100");

  // Recipe
  const [recipeName, setRecipeName] = useState("Chocolate Chip Cookies");
  const [recipeImage, setRecipeImage] = useState("");
  const [recipeDescription, setRecipeDescription] = useState("Soft, chewy chocolate chip cookies ready in under an hour.");
  const [recipePrepTime, setRecipePrepTime] = useState("PT15M");
  const [recipeCookTime, setRecipeCookTime] = useState("PT12M");
  const [recipeYield, setRecipeYield] = useState("24 cookies");
  const [recipeCategory, setRecipeCategory] = useState("Dessert");
  const [recipeCuisine, setRecipeCuisine] = useState("American");
  const [recipeIngredients, setRecipeIngredients] = useState(
    "2 cups flour\n1 cup sugar\n2 eggs"
  );
  const [recipeInstructions, setRecipeInstructions] = useState(
    "Preheat oven to 375°F.\nMix dry ingredients in a bowl.\nBeat in eggs and butter.\nDrop spoonfuls onto a baking sheet.\nBake for 10-12 minutes until golden."
  );

  const json = useMemo(() => {
    let obj: Record<string, unknown> = { "@context": "https://schema.org" };

    if (type === "Article") {
      obj = {
        ...obj,
        "@type": "Article",
        headline: articleTitle,
        author: { "@type": authorType, name: articleAuthor },
        datePublished: articleDate,
        ...(articleModified ? { dateModified: articleModified } : {}),
        ...(articlePublisher.trim()
          ? { publisher: { "@type": "Organization", name: articlePublisher.trim(), ...(publisherLogo.trim() && isHttpUrl(publisherLogo) ? { logo: { "@type": "ImageObject", url: publisherLogo.trim() } } : {}) } }
          : {}),
        ...(articleImage.trim() ? { image: articleImage.trim() } : {}),
      };
    } else if (type === "Product") {
      obj = {
        ...obj,
        "@type": "Product",
        name: productName,
        offers: {
          "@type": "Offer",
          price: productPrice,
          priceCurrency: productCurrency,
          availability: `https://schema.org/${productAvailability}`,
        },
        ...(productRating &&
          productReviewCount && {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: productRating,
              reviewCount: productReviewCount,
            },
          }),
      };
    } else if (type === "FAQPage") {
      const pairs = faqPairs
        .filter(p => p.q.trim() && p.a.trim())
        .map(p => ({
          "@type": "Question",
          name: p.q.trim(),
          acceptedAnswer: { "@type": "Answer", text: p.a.trim() },
        }));
      obj = { ...obj, "@type": "FAQPage", mainEntity: pairs };
    } else if (type === "LocalBusiness") {
      obj = {
        ...obj,
        "@type": "LocalBusiness",
        name: bizName,
        address: toPostalAddress(bizAddress),
        telephone: bizPhone,
      };
    } else if (type === "Recipe") {
      obj = {
        ...obj,
        "@type": "Recipe",
        name: recipeName,
        ...(recipeImage.trim() ? { image: recipeImage.trim() } : {}),
        description: recipeDescription,
        prepTime: recipePrepTime,
        cookTime: recipeCookTime,
        totalTime: sumISODurations(recipePrepTime, recipeCookTime),
        recipeYield: recipeYield,
        recipeCategory: recipeCategory,
        recipeCuisine: recipeCuisine,
        recipeIngredient: recipeIngredients.split("\n").filter(Boolean),
        recipeInstructions: recipeInstructions
          .split("\n")
          .filter(Boolean)
          .map(step => ({ "@type": "HowToStep", text: step.trim() })),
      };
    }

    return serializeJsonLd(obj);
  }, [
    type,
    articleTitle,
    articleAuthor,
    articleDate,
    articleImage,
    articleModified,
    articlePublisher,
    publisherLogo,
    authorType,
    productName,
    productPrice,
    productCurrency,
    productAvailability,
    productRating,
    productReviewCount,
    faqPairs,
    bizName,
    bizAddress,
    bizPhone,
    recipeName,
    recipeImage,
    recipeDescription,
    recipePrepTime,
    recipeCookTime,
    recipeCategory,
    recipeCuisine,
    recipeInstructions,
    recipeYield,
    recipeIngredients,
  ]);

  const { logUsageNow } = useLogUsageOnAction();

  const copy = async () => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    void copyText(
      `<script type="application/ld+json">\n${json}\n</script>`
      , "Copied script tag to clipboard");
  };

  // Required fields, per schema type -- schema.org validators reject
  // (or search engines simply ignore) markup missing these, so gating
  // the copy step here catches an incomplete schema before it gets
  // pasted somewhere and silently fails to do anything.
  const missingRequired = (() => {
    if (type === "Article") return !articleTitle.trim() || !articleAuthor.trim();
    if (type === "Product") return !productName.trim() || !productPrice.trim();
    if (type === "FAQPage") return !faqPairs.some(p => p.q.trim() && p.a.trim());
    if (type === "LocalBusiness") return !bizName.trim() || !bizAddress.trim();
    if (type === "Recipe") return !recipeName.trim() || !recipeIngredients.trim() || !recipeInstructions.trim();
    return false;
  })();

  // Not required, but Google's Article guidance recommends them, so say so rather than silently leaving them out.
  const recommended: string[] = [];
  const logoInvalid = type === "Article" && !!articlePublisher.trim() && !!publisherLogo.trim() && !isHttpUrl(publisherLogo);
  const imageInvalid = (type === "Article" && !!articleImage.trim() && !isHttpUrl(articleImage)) || (type === "Recipe" && !!recipeImage.trim() && !isHttpUrl(recipeImage));
  if (type === "Article") {
    if (!articlePublisher.trim()) recommended.push("publisher (organization name and logo)");
    if (!articleModified) recommended.push("dateModified");
    if (!articleImage.trim()) recommended.push("an image");
  } else if (type === "Recipe") {
    if (!recipeImage.trim()) recommended.push("an image (required for recipe rich results)");
  } else if (type === "Product") {
    recommended.push(...(productRating && productReviewCount ? [] : ["aggregateRating (only if you have real reviews)"]));
  }

  // Was useLogToolUsage(missingRequired, undefined, { ownActions: true }) -- exactly backwards: gated
  // on "required fields are NOT filled in", the opposite of "has a
  // real result". copy() above is now the real gate, awaited before
  // the clipboard write, same pattern as every other discrete-action
  // tool this round.

  return (
    <ToolContainer>
      <Label className="mb-2 block">Schema Type</Label>
      <Select value={type} onValueChange={v => setType(v as SchemaType)}>
        <SelectTrigger className="mb-4">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Article">Article</SelectItem>
          <SelectItem value="Product">Product</SelectItem>
          <SelectItem value="FAQPage">FAQ Page</SelectItem>
          <SelectItem value="LocalBusiness">Local Business</SelectItem>
          <SelectItem value="Recipe">Recipe</SelectItem>
        </SelectContent>
      </Select>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          {type === "Article" && (
            <>
              <div>
                <Label className="mb-1 block text-sm">Headline</Label>
                <Input aria-label="Headline"
                  value={articleTitle}
                  onChange={e => setArticleTitle(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Author</Label>
                <Input aria-label="Author"
                  value={articleAuthor}
                  onChange={e => setArticleAuthor(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Date Published</Label>
                <Input aria-label="Date Published"
                  type="date"
                  value={articleDate}
                  onChange={e => setArticleDate(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Author is a</Label>
                <select className="border border-border rounded-md bg-background px-2 py-2 text-sm w-full" value={authorType} onChange={e => setAuthorType(e.target.value as "Person" | "Organization")}>
                  <option value="Person">Person</option>
                  <option value="Organization">Organization</option>
                </select>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Date Modified (optional)</Label>
                <Input aria-label="Date Modified (optional)" type="date" value={articleModified} onChange={e => setArticleModified(e.target.value)} />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Publisher (organization name, optional)</Label>
                <Input aria-label="Publisher (organization name, optional)" value={articlePublisher} onChange={e => setArticlePublisher(e.target.value)} />
              </div>
              {articlePublisher.trim() && (
                <div>
                  <Label className="mb-1 block text-sm">Publisher logo URL (optional)</Label>
                  <Input aria-label="Publisher logo URL (optional)" value={publisherLogo} onChange={e => setPublisherLogo(e.target.value)} placeholder="https://…/logo.png" aria-invalid={logoInvalid} />
                  {logoInvalid && <p role="alert" className="text-xs text-destructive mt-1">Logo URL is not valid, so the logo is left out. Use a full https:// address.</p>}
                </div>
              )}
              <div>
                <Label className="mb-1 block text-sm">Image URL</Label>
                <Input aria-label="Image URL"
                  value={articleImage}
                  onChange={e => setArticleImage(e.target.value)}
                  placeholder="https://yoursite.com/image.jpg"
                  aria-invalid={imageInvalid}
                />
                {imageInvalid && <p role="alert" className="text-xs text-destructive mt-1">Image URL is not valid. Use a full https:// address.</p>}
              </div>
            </>
          )}
          {type === "Product" && (
            <>
              <div>
                <Label className="mb-1 block text-sm">Product Name</Label>
                <Input aria-label="Product Name"
                  value={productName}
                  onChange={e => setProductName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-sm">Price</Label>
                  <Input aria-label="Price"
                    value={productPrice}
                    onChange={e => setProductPrice(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-sm">Currency</Label>
                  <Input aria-label="Currency"
                    value={productCurrency}
                    onChange={e => setProductCurrency(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Availability</Label>
                <Select
                  value={productAvailability}
                  onValueChange={setProductAvailability}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="InStock">In Stock</SelectItem>
                    <SelectItem value="OutOfStock">Out of Stock</SelectItem>
                    <SelectItem value="PreOrder">Pre-Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-sm">Rating (1-5)</Label>
                  <Input aria-label="Rating (1-5)"
                    value={productRating}
                    onChange={e => setProductRating(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-sm">Review Count</Label>
                  <Input aria-label="Review Count"
                    value={productReviewCount}
                    onChange={e => setProductReviewCount(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
          {type === "FAQPage" && (
            <div className="space-y-3">
              <Label className="mb-1 block text-sm">Questions &amp; Answers</Label>
              {faqPairs.map(pair => (
                <div key={pair.id} className="p-3 rounded-lg border space-y-2">
                  <div className="flex items-start gap-2">
                    <Input
                      value={pair.q}
                      onChange={e => updateFaqPair(pair.id, "q", e.target.value)}
                      placeholder="Question"
                      className="flex-1"
                    />
                    <Button size="icon" variant="ghost" className="shrink-0" onClick={() => removeFaqPair(pair.id)} disabled={faqPairs.length === 1}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Textarea
                    value={pair.a}
                    onChange={e => updateFaqPair(pair.id, "a", e.target.value)}
                    placeholder="Answer"
                    className="min-h-[60px] text-sm"
                  />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addFaqPair} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add Question
              </Button>
            </div>
          )}
          {type === "LocalBusiness" && (
            <>
              <div>
                <Label className="mb-1 block text-sm">Business Name</Label>
                <Input aria-label="Business Name"
                  value={bizName}
                  onChange={e => setBizName(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Address (street, city, state zip)</Label>
                <Input aria-label="Address (street, city, state zip)"
                  value={bizAddress}
                  onChange={e => setBizAddress(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Phone</Label>
                <Input aria-label="Phone"
                  value={bizPhone}
                  onChange={e => setBizPhone(e.target.value)}
                />
              </div>
            </>
          )}
          {type === "Recipe" && (
            <>
              <div>
                <Label className="mb-1 block text-sm">Recipe Name</Label>
                <Input aria-label="Recipe Name"
                  value={recipeName}
                  onChange={e => setRecipeName(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Image URL (required for rich results)</Label>
                <Input aria-label="Image URL (required for rich results)"
                  value={recipeImage}
                  onChange={e => setRecipeImage(e.target.value)}
                  placeholder="https://yoursite.com/cookies.jpg"
                  aria-invalid={imageInvalid}
                />
                {imageInvalid && <p role="alert" className="text-xs text-destructive mt-1">Image URL is not valid. Use a full https:// address.</p>}
              </div>
              <div>
                <Label className="mb-1 block text-sm">Description</Label>
                <Input aria-label="Description"
                  value={recipeDescription}
                  onChange={e => setRecipeDescription(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-sm">
                    Prep Time (ISO 8601, e.g. PT15M)
                  </Label>
                  <Input
                    value={recipePrepTime}
                    onChange={e => setRecipePrepTime(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-sm">
                    Cook Time (ISO 8601, e.g. PT12M)
                  </Label>
                  <Input
                    value={recipeCookTime}
                    onChange={e => setRecipeCookTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-sm">Yield</Label>
                  <Input aria-label="Yield"
                    value={recipeYield}
                    onChange={e => setRecipeYield(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-sm">Category</Label>
                  <Input aria-label="Category"
                    value={recipeCategory}
                    onChange={e => setRecipeCategory(e.target.value)}
                    placeholder="e.g. Dessert, Main Course"
                  />
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Cuisine</Label>
                <Input aria-label="Cuisine"
                  value={recipeCuisine}
                  onChange={e => setRecipeCuisine(e.target.value)}
                  placeholder="e.g. Italian, Mexican"
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">
                  Ingredients (one per line)
                </Label>
                <Textarea
                  value={recipeIngredients}
                  onChange={e => setRecipeIngredients(e.target.value)}
                  className="min-h-[120px] font-mono text-sm"
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">
                  Instructions (one step per line)
                </Label>
                <Textarea
                  value={recipeInstructions}
                  onChange={e => setRecipeInstructions(e.target.value)}
                  className="min-h-[120px] font-mono text-sm"
                />
              </div>
            </>
          )}
        </div>

        <div>
          <Label className="mb-1 block text-sm">Generated JSON-LD</Label>
          <Textarea aria-label="Generated JSON-LD"
            readOnly
            value={json}
            className="min-h-[350px] font-mono text-xs bg-muted/30"
          />
          {!missingRequired && recommended.length > 0 && (
            <NoticeBox className="mt-2 text-xs">Optional but recommended for richer results: {recommended.join(", ")}.</NoticeBox>
          )}
          {missingRequired && (
            <NoticeBox className="text-xs mt-2">
              Fill in the required fields above for this schema type before copying — incomplete
              markup is often ignored or rejected by search engines and schema validators.
            </NoticeBox>
          )}
          <Button className="mt-3" onClick={copy} disabled={missingRequired}>
            Copy as Script Tag
          </Button>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
